// Etappe 4.2 — Read-only change-history viewer for moderation pages
// Renders a small modal/popover with the post's change_history reversed (newest first).
// Used by AdminPanel for karttipps, foreningar, tjanstekort, evenemang, vardagskort.
//
// Props:
//   collection: "karttipps" | "foreningar" | "tjanstekort" | "evenemang" | "vardagskort"
//   docId:      uuid / slug of the post
//   onClose:    callback when user closes the viewer
import { useEffect, useState } from "react";
import axios from "axios";
import { X, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
function modHeaders() { return { Authorization: `Bearer ${sessionStorage.getItem("mod_token")}` }; }

function formatTimestamp(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("sv-SE");
  } catch {
    return iso;
  }
}

export default function ChangeHistoryViewer({ collection, docId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    axios
      .get(`${API}/mod/history/${collection}/${docId}`, { headers: modHeaders() })
      .then((r) => { if (mounted) { setHistory(r.data?.history || []); setError(null); } })
      .catch((e) => { if (mounted) setError(e?.response?.data?.detail || "Kunde inte hämta historik"); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [collection, docId]);

  return (
    <div
      data-testid="change-history-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 px-3"
      onClick={onClose}
    >
      <div
        className="bg-parchment border border-warm-border rounded-md shadow-lg w-full max-w-lg max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-warm-border px-4 py-3">
          <h3 className="font-heading text-base font-semibold text-ink flex items-center gap-2">
            <Clock size={16} className="text-pine" /> Ändringshistorik
          </h3>
          <button
            data-testid="close-change-history"
            onClick={onClose}
            className="p-1 text-bark/60 hover:text-ink rounded transition-colors"
            title="Stäng"
          >
            <X size={18} />
          </button>
        </div>
        <div className="overflow-y-auto px-4 py-3 text-sm">
          {loading && <p className="text-bark/60 italic">Laddar...</p>}
          {!loading && error && <p className="text-falu">{error}</p>}
          {!loading && !error && history.length === 0 && (
            <p data-testid="change-history-empty" className="text-bark/65 italic">Ingen ändringshistorik ännu.</p>
          )}
          {!loading && !error && history.length > 0 && (
            <ol data-testid="change-history-list" className="flex flex-col gap-2">
              {history.map((h, i) => (
                <li
                  key={i}
                  data-testid={`change-history-entry-${i}`}
                  className="border-l-2 border-pine/40 pl-3 py-1"
                >
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <span className="font-mono text-xs text-bark/70">{formatTimestamp(h.changed_at)}</span>
                    <span className="font-medium text-ink">av {h.changed_by || "okänd"}</span>
                  </div>
                  {h.note && (
                    <p className="text-bark/85 text-xs mt-0.5 whitespace-pre-wrap">{h.note}</p>
                  )}
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}
